export type SqftMode = 'box' | 'sqft';
export const UNITS = ['Sq.ft.', 'Pcs', 'Bag'] as const;
export type Unit = (typeof UNITS)[number];
export function isAreaUnit(u: string): boolean {
  return u === 'Sq.ft.';
}

// round to 2dp
const r2 = (x: number) => Math.round(x * 100) / 100;
const isWhole = (x: number) => Math.abs(x - Math.round(x)) < 1e-6;

export function resolveSqftQuantity(
  rawInput: number,
  mode: SqftMode,
  sqftPerBox: number,
  sqftPerPiece: number,
): number {
  // No box size configured → the qty input IS the area in sq ft (entered directly).
  if (!(sqftPerBox > 0)) return r2(Math.max(0, rawInput));
  if (mode === 'box') {
    const boxes = Math.max(0, rawInput);
    return r2(boxes * sqftPerBox);
  }
  // sqft mode (sqftPerBox > 0 guaranteed here)
  const A = rawInput;
  if (!(A > 0)) return 0;
  if (sqftPerPiece > 0 && A < sqftPerPiece) return r2(sqftPerPiece); // min one piece
  if (isWhole(A / sqftPerPiece)) return r2(A); // exact whole pieces → keep
  const boxesFloat = A / sqftPerBox;
  const whole = Math.floor(boxesFloat);
  const frac = boxesFloat - whole;
  const boxes = Math.max(1, frac >= 0.3 ? whole + 1 : whole);
  return r2(boxes * sqftPerBox);
}

// Human label for a resolved area, e.g. "2 boxes", "5 pieces", or "X sq ft".
export function describeSqftQuantity(
  area: number,
  sqftPerBox: number,
  sqftPerPiece: number | null,
): string {
  if (sqftPerBox > 0 && isWhole(area / sqftPerBox)) {
    const n = Math.round(area / sqftPerBox);
    return `${n} box${n === 1 ? '' : 'es'}`;
  }
  if (sqftPerPiece && sqftPerPiece > 0 && isWhole(area / sqftPerPiece)) {
    const n = Math.round(area / sqftPerPiece);
    return `${n} piece${n === 1 ? '' : 's'}`;
  }
  return `${area.toFixed(2)} sq ft`;
}

// Box-count label for a line beside its sq ft. Exact whole counts show plainly ("16 boxes");
// non-whole counts round with a tilde ("~4 boxes"); sub-box rounds to "<1 box"; "" when no box size.
export function boxCountLabel(area: number, sqftPerBox: number): string {
  if (!(sqftPerBox > 0) || !(area > 0)) return '';
  const n = area / sqftPerBox;
  const r = Math.round(n);
  const whole = Math.abs(n - r) < 0.001;
  if (whole) return `${r} ${r === 1 ? 'box' : 'boxes'}`;
  if (r < 1) return '<1 box';
  return `~${r} ${r === 1 ? 'box' : 'boxes'}`;
}
